import { useState, useRef, useCallback, useEffect } from 'react';
import { api } from '../api/client';

export type RowStatus = 'pending' | 'processing' | 'created' | 'failed' | 'skipped';

export interface BulkRecipient {
  name: string;
  email: string;
  rowIndex: number;
}

export interface BulkRowResult {
  recipient: BulkRecipient;
  status: RowStatus;
  awardId?: string;
  error?: string;
}

export type ProcessorStatus = 'idle' | 'running' | 'paused' | 'done' | 'cancelled';

interface ProcessorState {
  badgeId: string;
  recipients: BulkRecipient[];
  results: BulkRowResult[];
  currentIndex: number;
  status: ProcessorStatus;
  issuedOn: number;
  expires?: number;
  sendEmail: boolean;
  startedAt: number;
}

const STORAGE_KEY = 'bulk-awards-state';
const DELAY_MS = 1000;
const RETRY_DELAY_MS = 5000;
const MAX_RETRIES = 3;

function loadState(): ProcessorState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as ProcessorState;
    // Only resume if status was 'running' or 'paused' (not done/cancelled)
    if (state.status === 'running') state.status = 'paused'; // was interrupted
    if (state.status === 'paused') return state;
    return null;
  } catch {
    return null;
  }
}

function saveState(state: ProcessorState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or unavailable — continue without persistence
  }
}

function clearState() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

function isRateLimitError(err: unknown): boolean {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return msg.includes('429') || msg.includes('rate limit') || msg.includes('too many');
  }
  return false;
}

function isCreditsError(err: unknown): boolean {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return msg.includes('402') || msg.includes('credit') || msg.includes('insufficient');
  }
  return false;
}

export function useBulkAwardProcessor() {
  const [state, setState] = useState<ProcessorState | null>(null);
  const abortRef = useRef(false);
  // Distinguishes a cancel from a pause: both stop the loop via abortRef, but a
  // cancel must NOT be re-written to 'paused' (nor re-persisted) when the
  // in-flight award resolves after the click.
  const cancelRef = useRef(false);
  const runningRef = useRef(false);

  // Check for resumable state on mount
  const [hasResumableJob, setHasResumableJob] = useState(() => !!loadState());

  useEffect(() => {
    setHasResumableJob(!!loadState());
  }, []);

  function initProcess(
    badgeId: string,
    recipients: BulkRecipient[],
    issuedOn: number,
    expires: number | undefined,
    sendEmail: boolean
  ) {
    const results: BulkRowResult[] = recipients.map((r) => ({
      recipient: r,
      status: 'pending' as RowStatus,
    }));
    const newState: ProcessorState = {
      badgeId,
      recipients,
      results,
      currentIndex: 0,
      status: 'idle',
      issuedOn,
      expires,
      sendEmail,
      startedAt: Date.now(),
    };
    setState(newState);
    saveState(newState);
    return newState;
  }

  function resumeSavedJob() {
    const saved = loadState();
    if (saved) {
      setState(saved);
      return saved;
    }
    return null;
  }

  const processAwards = useCallback(async (currentState: ProcessorState) => {
    if (runningRef.current) return;
    runningRef.current = true;
    abortRef.current = false;
    cancelRef.current = false;

    const s: ProcessorState = {
      ...currentState,
      status: 'running',
      results: [...currentState.results],
    };
    setState({ ...s });
    saveState(s);

    for (let i = s.currentIndex; i < s.recipients.length; i++) {
      if (abortRef.current) {
        // cancel() already set status 'cancelled' + cleared persistence — don't
        // clobber it with 'paused'. A plain pause writes the paused checkpoint.
        if (!cancelRef.current) {
          s.status = 'paused';
          s.currentIndex = i;
          setState({ ...s });
          saveState(s);
        }
        runningRef.current = false;
        return;
      }

      // Update current row to processing
      s.results[i] = { ...s.results[i], status: 'processing' };
      s.currentIndex = i;
      setState({ ...s });

      const recipient = s.recipients[i];
      let retries = 0;
      let success = false;

      while (retries <= MAX_RETRIES && !success) {
        try {
          const awardResult = await api.createAward({
            badgeId: s.badgeId,
            recipient: { name: recipient.name, email: recipient.email },
            issuedOn: s.issuedOn,
            expires: s.expires,
          });

          s.results[i] = { recipient, status: 'created', awardId: awardResult.awardId };
          success = true;

          // Send email if enabled
          if (s.sendEmail && awardResult.awardId) {
            try {
              // sendAward puts the id in the URL path — must be the GUID.
              const guid = awardResult.awardId.split('/').pop() || awardResult.awardId;
              await api.sendAward(guid, recipient.email);
            } catch {
              // Email send failure is non-critical — award was already created
            }
          }
        } catch (err: unknown) {
          if (isRateLimitError(err) && retries < MAX_RETRIES) {
            retries++;
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
            continue;
          }

          if (isCreditsError(err)) {
            // Stop processing — user needs to top up credits
            s.results[i] = { recipient, status: 'failed', error: 'Insufficient credits' };
            s.status = 'paused';
            s.currentIndex = i + 1;
            setState({ ...s });
            saveState(s);
            runningRef.current = false;
            return;
          }

          const errorMessage =
            err instanceof Error ? err.message : 'Unknown error';
          s.results[i] = { recipient, status: 'failed', error: errorMessage };
          success = true; // move on, don't retry non-rate-limit errors
        }
      }

      if (!success) {
        s.results[i] = {
          recipient,
          status: 'failed',
          error: 'Max retries exceeded (rate limited)',
        };
      }

      // If the user cancelled while this award was in flight, stop now WITHOUT
      // persisting — cancel() already set 'cancelled' and cleared storage, and
      // re-saving here would resurrect the run (local s.status is still 'running').
      if (cancelRef.current) {
        runningRef.current = false;
        return;
      }

      s.currentIndex = i + 1;
      setState({ ...s });
      saveState(s);

      // Delay between awards to avoid rate limiting
      if (i < s.recipients.length - 1 && !abortRef.current) {
        await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      }
    }

    s.status = 'done';
    setState({ ...s });
    saveState(s);
    runningRef.current = false;
  }, []);

  function start(st: ProcessorState) {
    processAwards(st);
  }

  function pause() {
    abortRef.current = true;
  }

  function resume() {
    if (state && state.status === 'paused') {
      processAwards(state);
    }
  }

  function cancel() {
    abortRef.current = true;
    cancelRef.current = true;
    if (state) {
      const cancelled: ProcessorState = {
        ...state,
        status: 'cancelled',
        results: state.results.map((r) =>
          r.status === 'pending' || r.status === 'processing'
            ? { ...r, status: 'skipped' as RowStatus }
            : r
        ),
      };
      setState(cancelled);
      clearState();
    }
    runningRef.current = false;
  }

  function reset() {
    abortRef.current = true;
    runningRef.current = false;
    setState(null);
    clearState();
    setHasResumableJob(false);
  }

  // Computed stats
  const created =
    state?.results.filter((r) => r.status === 'created').length ?? 0;
  const failed =
    state?.results.filter((r) => r.status === 'failed').length ?? 0;
  const pending =
    state?.results.filter(
      (r) => r.status === 'pending' || r.status === 'processing'
    ).length ?? 0;
  const total = state?.recipients.length ?? 0;
  const progress =
    total > 0 ? Math.round(((created + failed) / total) * 100) : 0;

  return {
    state,
    hasResumableJob,
    resumeSavedJob,
    initProcess,
    start,
    pause,
    resume,
    cancel,
    reset,
    stats: { created, failed, pending, total, progress },
  };
}
